/**
 * MobileHeader — Clean, minimal mobile app header.
 *
 * Replaces the full desktop TopHeader on mobile devices.
 * Shows: Back button (on sub-pages), page title, profile avatar.
 * Much cleaner and more native-feeling than the desktop header.
 */

import React, { useMemo, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useDesignSystemStore } from '../store/useDesignSystemStore';
import { TOPBAR_HEIGHT } from '../theme/tokens';

// Map paths to friendly mobile page titles
const PAGE_TITLES = {
  '/home': 'ICT Ops',
  '/tickets': 'Tickets',
  '/assets': 'Assets',
  '/notifications': 'Notifications',
  '/profile': 'Profile',
  '/hardware/requests': 'Hardware Requests',
  '/hardware/catalog': 'Hardware Catalog',
  '/hardware/dashboard': 'Hardware Dashboard',
  '/software/requests': 'Software Requests',
  '/software/catalog': 'Software Catalog',
  '/problems': 'Problems',
  '/knowledge': 'Knowledge Base',
  '/cmdb': 'CMDB',
  '/vendors': 'Vendors',
  '/warehouse': 'Warehouse',
  '/warehouse/stock': 'Stock Explorer',
  '/locations': 'Locations',
  '/reports': 'Reports',
  '/maintenance': 'Maintenance',
  '/maintenance-mgmt': 'Maintenance',
  '/help': 'Help',
  '/service-portal': 'Service Portal',
  '/audit-logs': 'Audit Logs',
  '/users': 'Users',
  '/departments': 'Departments',
  '/ticket-pool': 'Ticket Pool',
  '/allocation-center': 'Allocation',
  '/server-requests': 'Server Requests',
  '/vpn-requests': 'VPN Requests',
};

function getPageTitle(pathname) {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  const match = Object.keys(PAGE_TITLES)
    .sort((a, b) => b.length - a.length) // Longest prefix first
    .find((key) => key !== '/' && pathname.startsWith(key));
  if (match) return PAGE_TITLES[match];
  const segments = pathname.split('/').filter(Boolean);
  const last = segments[segments.length - 1] || '';
  if (/^[a-f0-9]{24}$/i.test(last)) {
    const parent = segments[segments.length - 2] || '';
    return parent.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }
  return last.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || 'ICT Ops';
}

export default function MobileHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const mode = useDesignSystemStore((s) => s.mode);
  const toggleMode = useDesignSystemStore((s) => s.toggleMode);

  const [menuAnchor, setMenuAnchor] = useState(null);

  const title = useMemo(() => getPageTitle(location.pathname), [location.pathname]);
  const isHome = location.pathname === '/home' || location.pathname === '/';

  const handleBack = () => window.history.back();
  const handleMenuOpen = (e) => setMenuAnchor(e.currentTarget);
  const handleMenuClose = () => setMenuAnchor(null);

  const handleLogout = useCallback(async () => {
    handleMenuClose();
    await logout();
    navigate('/login');
  }, [logout, navigate]);

  const initials = user?.name
    ? user.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()
    : 'U';

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: TOPBAR_HEIGHT,
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        px: 1,
        zIndex: 1100,
        // Safe area for notch
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      {/* Left: Back or spacer */}
      {!isHome ? (
        <IconButton onClick={handleBack} size="small" aria-label="Go back" sx={{ mr: 0.5 }}>
          <BackIcon sx={{ fontSize: 22 }} />
        </IconButton>
      ) : (
        <Box sx={{ width: 40 }} />
      )}

      {/* Center: Title */}
      <Typography
        sx={{
          flex: 1,
          textAlign: 'center',
          fontSize: '1rem',
          fontWeight: 700,
          color: 'text.primary',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {title}
      </Typography>

      {/* Right: Avatar/Menu */}
      <IconButton onClick={handleMenuOpen} size="small" aria-label="User menu">
        <Avatar
          sx={{
            width: 32,
            height: 32,
            fontSize: '0.75rem',
            fontWeight: 600,
            bgcolor: 'primary.main',
            color: '#fff',
          }}
        >
          {initials}
        </Avatar>
      </IconButton>

      {/* Profile menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{ sx: { minWidth: 200, borderRadius: '12px', mt: 1 } }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="body2" fontWeight={600}>{user?.name || 'User'}</Typography>
          <Typography variant="caption" color="text.secondary">{user?.role?.replace(/_/g, ' ')}</Typography>
        </Box>
        <Divider />
        <MenuItem onClick={() => { handleMenuClose(); navigate('/profile'); }}>
          <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Profile</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { handleMenuClose(); toggleMode(); }}>
          <ListItemIcon>
            {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText>{mode === 'dark' ? 'Light Mode' : 'Dark Mode'}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { handleMenuClose(); navigate('/org-settings'); }}>
          <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          <ListItemIcon><LogoutIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}
